import { useNavigate } from "react-router";
import { useCallback, useEffect, useState } from "react";
import db, { Key } from "../../database/database";
import { getAPIClient } from "../../apiClient";
import { infoLog } from "../../utils/logger";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import "./navbar.css";
import React from "react";
import { STAY_LOGGED_IN_PW, hasScreeningsEnabled } from "../../globals";
import { useBranding } from "../../branding/useBranding";
import { getAuth } from "firebase/auth";
import { useAtom } from "jotai";
import logout from "../../utils/logout";
import { userAtom, highContrastAtom } from "../../jotai/atoms";
import { vibrateDevice } from "../../utils/capacitor";
import { Capacitor } from "@capacitor/core";
import { Dialog } from "@capacitor/dialog";
import { Modal } from "../modals/Modal";

export function Navbar() {
  const [stayLogged, setStayLogged] = useState<boolean | null>(null);
  const [user] = useAtom(userAtom);
  const [highContrast, setHighContrast] = useAtom(highContrastAtom);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const isWeb = Capacitor.getPlatform() === "web";

  const branding = useBranding();

  const navigate = useNavigate();

  const { t } = useTranslation();
  const [menuIsActive, setMenuIsActive] = useState(false);

  const [navBarUserName, setNavBarUserName] = useState<string>("");
  const [email, setEmail] = useState<string>("");

  const navbarMenuRef = React.useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const checkLoggedInStatus = async () => {
      try {
        await db().unlock(STAY_LOGGED_IN_PW);
        setStayLogged(true);
      } catch (e: unknown) {
        setStayLogged(false);
      }
    };

    checkLoggedInStatus();
  }, []);

  useEffect(() => {
    const closeMenu = (event: MouseEvent) => {
      if (
        menuIsActive &&
        navbarMenuRef.current &&
        !navbarMenuRef.current.contains(event.target as Node)
      ) {
        setMenuIsActive(false);
      }
    };

    document.body.addEventListener("mousedown", closeMenu);

    return () => {
      document.body.removeEventListener("mousedown", closeMenu);
    };
  }, [menuIsActive]);

  const handleClick = () => {
    setMenuIsActive(false);
  };

  async function signOut() {
    await logout(getAPIClient(), getAuth());
    navigate("/");
  }

  const handleLogoutClick = async () => {
    if (isWeb) {
      setShowLogoutModal(true);
    } else {
      const { value } = await Dialog.confirm({
        title: t("menu.sign_out_confirmation_title"),
        message: t("menu.sign_out_confirmation_message"),
        okButtonTitle: t("generic.ok"),
        cancelButtonTitle: t("generic.cancel"),
      });
      if (value) {
        await signOut();
      }
    }
  };

  const toggleHighContrast = async () => {
    if (!highContrast) await vibrateDevice();
    setHighContrast(!highContrast);
  };

  useEffect(() => {
    const userName = `${user?.first_name || ""} ${user?.last_name || ""}`;

    if (navBarUserName !== userName) {
      setEmail(user?.email ?? "");
      setNavBarUserName(userName);
    }
  }, [user]);

  return (
    <>
      {showLogoutModal && (
        <Modal
          id="navbar-logout-confirmation-modal"
          open={showLogoutModal}
          title={t("menu.sign_out_confirmation_title")}
          onCloseModal={() => setShowLogoutModal(false)}
          hasClose={true}
          actionButtons={
            <>
              <button className="btn info" onClick={() => setShowLogoutModal(false)}>
                {t("generic.cancel")}
              </button>
              <button className="btn btn-primary" onClick={signOut}>
                {t("menu.sign_out")}
              </button>
            </>
          }
        >
          <p>{t("menu.sign_out_confirmation_message")}</p>
        </Modal>
      )}
      <nav
        className="w-full flex pt-4"
        role="navigation"
        aria-label="main navigation"
        data-testid="navbar"
      >
        <div className="flex-1"></div>
        <div className="flex justify-between flex-6">
          <div className="text-white w-36 cursor-pointer">
            <a className="" onClick={() => navigate("/home/search")}>
              <img src={`/${branding.headerImage}`} height="28" alt="Docket logo" />
            </a>
          </div>
          {stayLogged === false && (
            <div className="text-white">
              <p className="text-sm">
                <b>{t("generic.warning")}:</b> {t("menu.log_out_warning")}
              </p>
            </div>
          )}
          <div>
            <ul className="flex items-center gap-1">
              <li className="my-auto">
                <Link
                  className="btn btn-ghost text-primary-content hover:text-base-content"
                  to="/home/search"
                  onClick={handleClick}
                >
                  <img className="w-8" src="/assets/images/Icon_Search.png" alt={"Search"} />
                  {t("immunizations.tab_bar_search_title")}
                </Link>
              </li>
              <li className="my-auto">
                <Link
                  className="btn btn-ghost text-primary-content hover:text-base-content"
                  to="/home/records"
                  onClick={handleClick}
                >
                  <img
                    className="w-8"
                    src="/assets/images/Icon_Immunizations.png"
                    alt={"Immunizations"}
                  />
                  {t("menu.immunizations")}
                </Link>
              </li>
              {hasScreeningsEnabled(user?.email || "") && (
                <li className="my-auto">
                  <Link
                    className="btn btn-ghost text-primary-content hover:text-base-content"
                    to="/home/records/screening"
                    onClick={handleClick}
                  >
                    <img className="w-8" src="/assets/images/Icon_Results.png" alt={"Partners"} />
                    {t("screening.tab_bar_screenings_title")}
                  </Link>
                </li>
              )}
              <li className="my-auto">
                <Link
                  className="btn btn-ghost text-primary-content hover:text-base-content"
                  to="/home/discover"
                  onClick={handleClick}
                >
                  <img className="w-8" src="/assets/images/Icon_Partners.png" alt={"Partners"} />
                  {t("menu.discover")}
                </Link>
              </li>
              <li className="my-auto">
                <Link
                  className="btn btn-ghost text-primary-content hover:text-base-content"
                  to="https://dockethealth.com/faqs"
                  onClick={handleClick}
                >
                  <img className="w-8" src="/assets/images/Icon_FAQs.png" alt={"FAQs"} />
                  FAQ
                </Link>
              </li>
              <div className="dropdown dropdown-end">
                <div
                  tabIndex={0}
                  role="button"
                  className="btn btn-primary btn-outline text-primary-content"
                >
                  <div className="flex gap-2">{navBarUserName}</div>
                  <i className="fa-solid fa-angle-down"></i>
                </div>
                <ul
                  tabIndex={-1}
                  className="dropdown-content menu bg-base-100 rounded-box z-1 w-fit border-primary border-2 p-2 mt-1 shadow-sm"
                >
                  <p className="p-2 font-semibold">{email}</p>
                  <li>
                    <label className="flex items-center justify-between cursor-pointer">
                      <span>Dark Mode</span>
                      <input
                        type="checkbox"
                        className="toggle toggle-primary"
                        checked={highContrast}
                        onChange={toggleHighContrast}
                        data-testid="navbar-high-contrast-toggle"
                      />
                    </label>
                  </li>
                  <li>
                    <Link to="/home/settings" onClick={handleClick}>
                      {t("menu.settings")}
                    </Link>
                  </li>
                  <li>
                    <a onClick={handleLogoutClick}>{t("menu.sign_out")}</a>
                  </li>
                </ul>
              </div>
            </ul>
          </div>
        </div>
        <div className="flex-1"></div>
      </nav>
    </>
  );
}
